import Popover from "@mui/material/Popover";
import cn from "classnames";
import copy from "copy-to-clipboard";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Meta } from "services/query/types";

import MoreVerticalIcon from "assets/icons/MoreVertical.svg?react";

import ContextMenu from "components/ContextMenu/ContextMenu";
import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import { formatCellValue } from "components/Datagrid/utils/formatCell";
import Tooltip from "components/Tooltip/Tooltip";

import { saveToFile } from "./saveToFile";

import styles from "./ExportMenu.module.scss";

interface ExportData {
  rows: Record<string, unknown>[];
  meta?: Meta[];
}

interface ExportMenuProps {
  exportData: ExportData;
  prefixElement?: React.ReactElement;
  showDisplayLimit?: boolean;
}

const ExportMenu = (props: ExportMenuProps) => {
  const { exportData, prefixElement, showDisplayLimit } = props;

  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorEl = useRef<HTMLDivElement | null>(null);

  if (!exportData.rows.length) {
    return null;
  }

  return (
    <>
      <Tooltip
        enterDelay={300}
        title={t("document_output.menu.more_options")}
        placement="top-start"
      >
        <div
          className={cn(styles.iconContainer, {
            [styles.active]: menuOpen,
          })}
          ref={menuAnchorEl}
          onClick={() => {
            setMenuOpen(true);
          }}
          data-testid="export-results-icon"
        >
          <MoreVerticalIcon />
        </div>
      </Tooltip>

      {menuOpen && (
        <Popover
          open={menuOpen}
          anchorEl={menuAnchorEl.current}
          onClose={() => {
            setMenuOpen(false);
          }}
          sx={{ transform: "translate(0px, -5px)" }}
          onClick={e => {
            e.stopPropagation();
          }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <ContextMenu>
            {!!prefixElement && (
              <>
                {prefixElement}
                <ContextMenuDivider />
              </>
            )}
            <ContextMenuItem
              key="copy-results-to-clipboard"
              text={t("export_menu.copy_results")}
              testId="copy-results-to-clipboard"
              disabledTooltip={
                showDisplayLimit ? t("export_menu.copy_disclaimer") : ""
              }
              onClick={() => {
                setMenuOpen(false);
                const headers = Object.keys(exportData.rows[0]);
                const rows = [
                  headers.join("\t"),
                  ...exportData.rows.map(row =>
                    headers
                      .map((header: string) => {
                        const type = exportData?.meta
                          ?.find(m => m.name === header)
                          ?.type?.toUpperCase();
                        return exportData?.meta
                          ? formatCellValue({
                              value: row[header] as string,
                              type,
                            })
                          : row[header];
                      })
                      .join("\t")
                  ),
                ];
                copy(rows.join("\n"));
              }}
            />
            <ContextMenuItem
              key="export-as-json"
              text={t("export_menu.export_json")}
              disabledTooltip={
                showDisplayLimit ? t("export_menu.export_disclaimer") : ""
              }
              testId="export-as-json"
              onClick={() => {
                setMenuOpen(false);
                saveToFile(exportData.rows, "JSON", ",")
                  .then(() => {})
                  .catch(e => {
                    console.log(e);
                  });
              }}
            />
            <ContextMenuItem
              key="export-as-csv"
              text={t("export_menu.export_csv")}
              disabledTooltip={
                showDisplayLimit ? t("export_menu.export_disclaimer") : ""
              }
              testId="export-as-csv"
              onClick={() => {
                setMenuOpen(false);
                saveToFile(exportData.rows, "CSV", ",")
                  .then(() => {})
                  .catch(e => {
                    console.log(e);
                  });
              }}
            />
          </ContextMenu>
        </Popover>
      )}
    </>
  );
};

export default ExportMenu;
